import { track } from "@amplitude/analytics-react-native";
import toast from "src/swsh-native/toast";
import { useCallback, useState } from "react";
import { amp } from "src/api/amplitude";
import haptic from "src/helpers/haptic";
import log from "src/helpers/log";
import { enumNever } from "src/shared/helpers/generalHelpers";
import { ShareContextType, useShareContext } from "../../contexts/ShareContext";
import CommonPrefetchedData from "../../helpers/PrefetchedData/CommonPrefetchedData";
import getUniqueTypes from "../../helpers/getUniqueTypes";
import { nativeShareImage, nativeShareString } from "../../helpers/nativeShare";
import { typeParseData } from "../../types";
import { ShareShareDataType } from "./shareShareHelpers";

const useShareShare = (context?: ShareContextType) => {
	const { prefetched, onComplete, onSuccess } = useShareContext(context);
	const [loading, setLoading] = useState(false);

	const handlePress = useCallback(
		async (
			data: ShareShareDataType,
		): Promise<{
			didShare: boolean;
		}> => {
			try {
				setLoading(true);
				track(amp.share.share, {
					types: getUniqueTypes(data),
					dataLength: data.length,
				});
				// Ok to just look at first item, since it's either all text or all images
				const item = data[0];
				if (!item) {
					return { didShare: false };
				}
				const type = item?.type;
				haptic("Light");
				const { didShare } = await (async () => {
					switch (type) {
						case "Text":
						case "Url":
							const body = type === "Text" ? item.text : item.url;
							return await nativeShareString(body);

						case "Image":
						case "LivePhoto":
							const prefetchedData: CommonPrefetchedData[] = [];
							for (const { url } of typeParseData(data, type)) {
								const data = prefetched.current[url];
								if (data) {
									prefetchedData.push(data);
								}
							}

							return await nativeShareImage(prefetchedData);

						default:
							return enumNever(type);
					}
				})();
				if (didShare) {
					onSuccess?.();
					if (item.successAmplitude) {
						track(item.successAmplitude.eventName, {
							...item.successAmplitude.eventProperties,
							shareType: type,
							shareAction: "Share",
						});
					}
				}
				return {
					didShare,
				};
			} catch (err) {
				if (err instanceof Error) {
					if (err.message === "User did not share") {
						return {
							didShare: false,
						};
					}
				}
				log.error({ err }, "ShareShare error");
				toast({
					preset: "error",
					title: "Something went wrong",
				});
				return {
					didShare: false,
				};
			} finally {
				setLoading(false);
				onComplete?.();
			}
		},
		[onComplete, onSuccess, prefetched],
	);

	return {
		loading,
		handlePress,
	};
};
export default useShareShare;
